"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Button } from "@/app/components/ui";
import VideoAccessGate from "@/app/components/VideoPlayer/VideoAccessGate.jsx";
import styles from "./HeroSection.module.css";

// The real embed player, rendered inline (same-origin) instead of via a
// cross-origin <iframe>. One instance is mounted at a time and re-keyed per
// tab, so exactly one video decoder is ever live (iOS only allows a small
// number — stacking seven iframes caused "media failed to decode" on
// app-switch). Because the player code is already in the page bundle, switching
// shows the poster instantly and boots fast — no iframe black-flash — and we
// can prefetch the other clips' segments (impossible across the iframe origin).
const VideoPlayerEmbedPreviewNoSSR = dynamic(
  () => import("@/app/components/VideoPlayer/VideoPlayerEmbedPreview.js"),
  { ssr: false, loading: () => null },
);

// ---------------------------------------------------------------------------
// Hero feature reel — a tabbed row of short feature demos. Each tab is a
// DISTINCT production embed (chapters menu, password gate, A/B variant, …);
// per-tab playback URL + embed settings are resolved server-side and fetched
// from /api/landing/hero-videos (keep HERO_VIDEO_IDS there in sync with this).
//
// To re-map a tab to a different video, change its `videoId` here AND in the
// API route's HERO_VIDEO_IDS allow-list.
// ---------------------------------------------------------------------------
// NOTE: the Introduction video (3f71ff54) is a camera-only 16:9 source, so it
// letterboxes slightly in the composite-shaped (1.6) frame — expected. The
// other six are composite recordings that fill the frame.
const TABS = [
  {
    id: "intro",
    label: "Introduction",
    videoId: "3f71ff54-7b85-40d3-a78f-8da237a7289f",
  },
  {
    id: "player",
    label: "Player",
    videoId: "0383e729-9525-421e-961b-8d99116dc725",
  },
  {
    id: "analytics",
    label: "Analytics",
    videoId: "9417c30c-9765-4ef6-95b0-260bf52e05b9",
  },
  {
    id: "engagement",
    label: "Engagement",
    videoId: "2309b17b-e43a-4fc7-b435-dcea3aa2f7a7",
  },
  {
    id: "abtest",
    label: "A/B testing",
    videoId: "3dfc443b-e053-48fb-8f48-bc3fda68a2a5",
  },
  {
    id: "chapters",
    label: "Chapters",
    videoId: "94d74c08-eb5b-4f80-8dff-334ecacea151",
  },
  {
    id: "password",
    label: "Password",
    videoId: "ec94991a-a9e3-43e3-ab43-b355dfae3618",
  },
];

/* Inline stroke icons (lucide-style) — one per tab. */
const ICONS = {
  intro: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  player: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  analytics: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 3v18h18" />
      <path d="M7 16v-3" />
      <path d="M12 16V9" />
      <path d="M17 16v-6" />
    </svg>
  ),
  engagement: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
  abtest: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M12 4v16" />
    </svg>
  ),
  chapters: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  ),
  password: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
};

// Best-effort HLS prefetch: warm the playlist(s) + init segment + first couple
// media segments into the browser HTTP cache so a cold tab boots fast. Same
// origin as the player's requests now (videos.flare.link, CORS:*), so hls.js
// gets cache hits on switch. Never throws — purely an optimisation.
async function prefetchHls(url) {
  try {
    if (!url || !url.includes(".m3u8")) return; // skip progressive MP4 (too big)
    const masterRes = await fetch(url);
    if (!masterRes.ok) return;
    const masterText = await masterRes.text();
    const masterLines = masterText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    // If this is a master playlist, descend into the first variant's media
    // playlist; otherwise treat the fetched text as the media playlist itself.
    let mediaUrl = url;
    let mediaText = masterText;
    const variant = masterLines.find(
      (l) => !l.startsWith("#") && l.includes(".m3u8"),
    );
    if (variant) {
      mediaUrl = new URL(variant, url).href;
      const mediaRes = await fetch(mediaUrl);
      if (!mediaRes.ok) return;
      mediaText = await mediaRes.text();
    }

    const mediaLines = mediaText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const targets = [];
    for (const line of mediaLines) {
      if (line.startsWith("#EXT-X-MAP:")) {
        const match = line.match(/URI="([^"]+)"/);
        if (match) targets.push(new URL(match[1], mediaUrl).href);
      }
    }
    const segments = mediaLines.filter((l) => !l.startsWith("#"));
    for (const seg of segments.slice(0, 2))
      targets.push(new URL(seg, mediaUrl).href);

    await Promise.all(targets.map((t) => fetch(t).catch(() => {})));
  } catch {
    // best-effort only
  }
}

export default function EmbedHeroSection({ isAuthenticated = false }) {
  const [active, setActive] = useState(TABS[0].id);
  // videoId -> { url, posterUrl, isPasswordProtected, videoInfo, player }
  const [dataById, setDataById] = useState(null);
  // Password tabs whose preview gate has been dismissed this session.
  const [gateDismissed, setGateDismissed] = useState(() => ({}));

  // Resolve per-tab playback URL + embed settings server-side (cached).
  useEffect(() => {
    let cancelled = false;
    fetch("/api/landing/hero-videos")
      .then((r) => (r.ok ? r.json() : { heroVideos: [] }))
      .then(({ heroVideos }) => {
        if (cancelled) return;
        const map = {};
        for (const v of heroVideos || []) map[v.videoId] = v;
        setDataById(map);
      })
      .catch(() => {
        if (!cancelled) setDataById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Once data is in, trickle-prefetch the non-active clips on idle so switching
  // to a cold tab starts from warm cache. Skipped under Save-Data.
  useEffect(() => {
    if (!dataById) return;
    const conn = typeof navigator !== "undefined" ? navigator.connection : null;
    if (conn && conn.saveData) return;

    const onIdle =
      typeof requestIdleCallback !== "undefined"
        ? (cb) => requestIdleCallback(cb, { timeout: 2500 })
        : (cb) => setTimeout(cb, 1);

    const urls = TABS.filter((t) => t.id !== TABS[0].id)
      .map((t) => dataById[t.videoId]?.url)
      .filter(Boolean);

    let cancelled = false;
    let index = 0;
    const pump = () => {
      if (cancelled || index >= urls.length) return;
      const next = urls[index++];
      prefetchHls(next).finally(() => {
        if (!cancelled) setTimeout(() => onIdle(pump), 600);
      });
    };
    const start = setTimeout(() => onIdle(pump), 1200);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, [dataById]);

  // Center a tapped tab WITHIN the strip only. We scroll the strip element
  // directly instead of using element.scrollIntoView({ inline: "center" }),
  // which also scrolls ancestor scrollports (and the document) and was
  // shifting the whole page sideways when tapping the rightmost tabs.
  const tablistRef = useRef(null);
  const centerTab = useCallback((button) => {
    const strip = tablistRef.current;
    if (!strip || !button) return;
    const target =
      button.offsetLeft - (strip.clientWidth - button.offsetWidth) / 2;
    const maxScroll = strip.scrollWidth - strip.clientWidth;
    strip.scrollTo({
      left: Math.max(0, Math.min(target, maxScroll)),
      behavior: "smooth",
    });
  }, []);

  const activeTab = TABS.find((t) => t.id === active) || TABS[0];
  const activeData = dataById?.[activeTab.videoId] || null;
  // High-res poster (frame 0) shown behind the player — paints instantly (even
  // before the data fetch resolves) so there's never a black gap on switch.
  const activePosterHq =
    activeData?.posterUrl ||
    `https://videos.flare.link/${activeTab.videoId}/thumbnail-hq.jpg`;

  const isProtected = Boolean(activeData?.isPasswordProtected);
  const showGate = isProtected && !gateDismissed[activeTab.videoId];
  const player = activeData?.player;
  const loopCfg = player?.loopConfig;

  return (
    <section className={styles.hero}>
      <div className={styles.ambientLight} />
      <div className={styles.ambientLightSecondary} />

      <div className={styles.heroInner}>
        <div className={styles.heroContent}>
          <h1 className={styles.title}>
            <span className={styles.desktopTitle}>
              Custom player. Blazing speed.{" "}
              <br className={styles.desktopBreak} />
              Unfair analytics. More conversions.
              <br className={styles.desktopBreak} />
            </span>
            <span className={styles.mobileTitle}>
              Blazing speed. <br className={styles.mobileBreak} />
              Custom player. <br className={styles.mobileBreak} />
              Unfair analytics.{" "}
            </span>
          </h1>

          <p className={styles.subtitle}>
            Customize your player, embed it anywhere, and track exactly how
            people watch.
          </p>
        </div>

        <div className={styles.ctaButtons}>
          <Button
            variant="attention"
            size="lg"
            className={styles.heroCta}
            href={isAuthenticated ? "/library" : "/register"}
          >
            {isAuthenticated ? "Go to library" : "Start free trial"}
          </Button>
        </div>

        {/* Wrapper lets the elevation bloom spill beyond the card edges
            without being clipped by the card's own overflow. */}
        <div className={styles.cardWrap}>
          <div className={styles.card}>
            <div
              ref={tablistRef}
              className={styles.tabs}
              role="tablist"
              aria-label="Feature demos"
            >
              {TABS.map((t) => {
                const isActive = t.id === active;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                    onClick={(event) => {
                      setActive(t.id);
                      // On the mobile scroll strip, pull the tapped tab toward
                      // the center so it's never half-clipped at an edge.
                      // No-op on desktop where the strip doesn't overflow.
                      centerTab(event.currentTarget);
                    }}
                  >
                    <span className={styles.tabIcon} aria-hidden="true">
                      {ICONS[t.id]}
                    </span>
                    <span className={styles.tabLabel}>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Single inline player. Re-keyed per tab so the previous decoder is
                torn down before the next boots (one decoder, ever). The poster
                (frame 0) sits behind it and bridges the brief boot. */}
            <div
              className={styles.preview}
              style={{
                backgroundImage: `url('${activePosterHq}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <link
                rel="preconnect"
                href="https://videos.flare.link"
                crossOrigin="anonymous"
              />

              {activeData?.url && !showGate && (
                <VideoPlayerEmbedPreviewNoSSR
                  key={activeTab.videoId}
                  url={activeData.url}
                  posterUrl={activeData.posterUrl}
                  suppressNativePoster
                  fitMode="cover"
                  videoInfo={activeData.videoInfo}
                  initialDuration={activeData.videoInfo?.duration}
                  initialDimensions={
                    activeData.videoInfo?.width && activeData.videoInfo?.height
                      ? {
                          width: activeData.videoInfo.width,
                          height: activeData.videoInfo.height,
                        }
                      : undefined
                  }
                  controlTheme={player?.controlTheme}
                  controlOptions={player?.controlOptions}
                  playerTheme={player?.playerTheme}
                  chapters={player?.chapters}
                  embedBorderRadiusCss={player?.embedBorderRadiusCss}
                  timelineProgression={player?.timelineProgression}
                  bufferProfile="minimal"
                  flareBrandingEnabled={false}
                  tapToToggleControls
                  loopOverlayConfig={
                    loopCfg
                      ? {
                          enabled: true,
                          startTime: loopCfg.startTime,
                          endTime: loopCfg.endTime,
                        }
                      : { enabled: false }
                  }
                />
              )}

              {showGate && (
                <VideoAccessGate
                  videoId={activeTab.videoId}
                  previewMode
                  backgroundColor={player?.passwordGate?.backgroundColor}
                  buttonColor={player?.passwordGate?.buttonColor}
                  onAccessGranted={() =>
                    setGateDismissed((prev) => ({
                      ...prev,
                      [activeTab.videoId]: true,
                    }))
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
